import DateInput from "Components/molecule/inputs/date-input"
import MultiSelect from "Components/molecule/inputs/multi-select"
import SelectInput, { SelectOption } from "Components/molecule/inputs/select-input"
import TextInput from "Components/molecule/inputs/text-input"
import TextAreaInput from "Components/molecule/inputs/textarea-inpuit"
import FormContainer from "Components/atoms/containers/form-container"
import { ConsultantAgreementFormValues } from "Components/templates/hr/document-generator/consultant-agreement-template"
import { FormikErrors, FormikTouched } from "formik"
import { JobTitle } from "models/org-chart-node"
import { ChangeEvent, useState } from "react"

const COL = "col-12 col-md-6 col-lg-4"
const COL_FULL = "col-12"



const CERTIFICATIONS = [
    "P.Eng. (APEGA)", "P.Eng. (EGBC)", "P.Eng. (APEGM)", "C.E.T.", "A.Sc.T.",
    "PMP", "CHRP", "CPA", "CSCMP", "Confined Space Entry",
    "Fall Protection", "H2S Alive", "First Aid Level 1", "First Aid Level 2",
];

const SCHEDULE_OPTIONS = [
    { value: "", label: "Select Schedule" },
    { value: "Regular Monday to Friday", label: "Regular Monday to Friday" },
    { value: "Project specific hours and schedule", label: "Project specific hours and schedule" },
    {
        value: "As-needed basis at a time mutually agreed upon before the commencement of each project",
        label: "As-needed basis at a time mutually agreed upon before the commencement of each project",
    },
];


interface ConsultantAgreementFormProps {
    caValues: ConsultantAgreementFormValues,
    caErrors: FormikErrors<ConsultantAgreementFormValues>
    caTouched: FormikTouched<ConsultantAgreementFormValues>
    editable: boolean,
    companyRepresentatives: SelectOption[],
    jobTitles: SelectOption[],
    certifications: SelectOption[],

    caHandleChange: (e: ChangeEvent<any>) => void;
    caHandleBlur: (e: React.FocusEvent<any>) => void;
    caSetFieldValue: (field: string, value: any) => void;
}


const ConsultantAgreementForm = ({
    companyRepresentatives,
    editable,
    caValues,
    caErrors,
    caTouched,
    jobTitles,
    certifications,

    caHandleBlur,
    caHandleChange,
    caSetFieldValue


}: ConsultantAgreementFormProps) => {
    const [pendingCert, setPendingCert] = useState("");

    const err = (field: keyof ConsultantAgreementFormValues) =>
        caTouched[field] ? (caErrors[field] as string | undefined) : undefined;

    const isTouched = (field: keyof ConsultantAgreementFormValues) => !!caTouched[field];

    const handleAddCertification = () => {
        if (!pendingCert || caValues.certifications.includes(pendingCert)) return;
        caSetFieldValue("certifications", [...caValues.certifications, pendingCert]);
        setPendingCert("");
    };

    const handleRemoveCertification = (cert: string) => {
        caSetFieldValue("certifications", caValues.certifications.filter((c) => c !== cert));
    };

    return <>
        <div className="form-section">
            <h5 className="form-section__title">Notices</h5>
            <FormContainer inputs={<>
                <SelectInput className={COL} disabled={!editable} name="companyRepresentative" label="Company Representative" required
                    value={caValues.companyRepresentative} options={companyRepresentatives}
                    error={!!caTouched.companyRepresentative && !!caErrors.companyRepresentative}
                    message={isTouched("companyRepresentative") ? err("companyRepresentative") : undefined} onBlur={caHandleBlur}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => caHandleChange(e)} />
                <TextInput className={COL} disabled={!editable} name="consultantRepresentative" label="Consultant Representative"
                    placeholder="Consultant Representative" required value={caValues.consultantRepresentative}
                    error={!!caTouched.consultantRepresentative && !!caErrors.consultantRepresentative}
                    message={isTouched("consultantRepresentative") ? err("consultantRepresentative") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
            </>} />
        </div>

        <div className="form-section">
            <h5 className="form-section__title">Term</h5>
            <FormContainer inputs={<>
                <DateInput className={COL} disabled={!editable} name="termEndDate" label="Term End Date"
                    placeholder="Term End Date" required value={caValues.termEndDate}
                    error={!!caTouched.termEndDate && !!caErrors.termEndDate}
                    message={isTouched("termEndDate") ? err("termEndDate") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
            </>} />
        </div>
        <div className="form-section">
            <h5 className="form-section__title">A.1 — Scope of Services</h5>
            <FormContainer inputs={<>
                <TextAreaInput disabled={!editable} name="duties" label="Duties"
                    placeholder="Describe the scope of services and duties" required
                    className={COL_FULL} value={caValues.duties} max={3000}
                    error={!!caTouched.duties && !!caErrors.duties}
                    message={isTouched("duties") ? err("duties") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
                <MultiSelect
                    className={COL_FULL}
                    label="Certifications & Designations"
                    options={certifications}
                    value={caValues.certifications.map((c) => ({ value: c, label: c }))}
                    onChange={(selected) => caSetFieldValue("certifications", selected.map((s) => s.value))}
                    placeholder="Select certifications / designations"
                    disabled={!editable}
                />
            </>} />
        </div>

        <div className="form-section">
            <h5 className="form-section__title">A.2 — Location &amp; Schedule</h5>
            <FormContainer inputs={<>
                <SelectInput className={COL} disabled={!editable} name="location" label="Work Location" required
                    value={caValues.location}
                    options={[
                        { value: "", label: "Select Location" },
                        { value: "RAM", label: "RAM" },
                        { value: "Site", label: "Site" },
                        { value: "Remote", label: "Remote" },
                    ]}
                    error={!!caTouched.location && !!caErrors.location}
                    message={isTouched("location") ? err("location") : undefined} onBlur={caHandleBlur}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => caHandleChange(e)} />
                {caValues.location === "Site" && (
                    <TextInput className={COL} disabled={!editable} name="siteAddress" label="Site Address"
                        placeholder="Site Address" required value={caValues.siteAddress}
                        error={!!caTouched.siteAddress && !!caErrors.siteAddress}
                        message={isTouched("siteAddress") ? err("siteAddress") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
                )}
                <SelectInput className={COL} disabled={!editable} name="schedule" label="Schedule" required
                    value={caValues.schedule} options={SCHEDULE_OPTIONS}
                    error={!!caTouched.schedule && !!caErrors.schedule}
                    message={isTouched("schedule") ? err("schedule") : undefined} onBlur={caHandleBlur}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => caHandleChange(e)} />
            </>} />
        </div>

        <div className="form-section">
            <h5 className="form-section__title">B.1 — Compensation</h5>
            <FormContainer inputs={<>
                <SelectInput
                    className={COL}
                    disabled={!editable}
                    name="descriptionTitle"
                    label="Job Title"
                    placeholder="Job Title"
                    required
                    value={caValues.descriptionTitle}
                    error={!!caTouched.descriptionTitle && !!caErrors.descriptionTitle}
                    message={isTouched("descriptionTitle") ? err("descriptionTitle") : undefined}
                    onBlur={caHandleBlur}
                    onChange={caHandleChange}
                    options={jobTitles}
                />
                <TextInput className={COL} disabled={!editable} name="hourlyRate" label="Hourly Rate"
                    placeholder="Hourly Rate" type="number" step="0.01" required value={caValues.hourlyRate}
                    error={!!caTouched.hourlyRate && !!caErrors.hourlyRate}
                    message={isTouched("hourlyRate") ? err("hourlyRate") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
                <TextInput className={COL} disabled={!editable} name="maxDailyRate" label="Maximum Daily Rate"
                    placeholder="Amount or N/A" value={caValues.maxDailyRate}
                    error={!!caTouched.maxDailyRate && !!caErrors.maxDailyRate}
                    message={isTouched("maxDailyRate") ? err("maxDailyRate") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
            </>} />
        </div>

        <div className="form-section">
            <h5 className="form-section__title">B.2 — Allowances</h5>
            <FormContainer inputs={<>
                <SelectInput className={COL} disabled={!editable} name="vehicleReimbursement"
                    label="Vehicle Reimbursement Required" required value={caValues.vehicleReimbursement}
                    options={[
                        { value: "", label: "Select" },
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                    ]}
                    error={!!caTouched.vehicleReimbursement && !!caErrors.vehicleReimbursement}
                    message={isTouched("vehicleReimbursement") ? err("vehicleReimbursement") : undefined} onBlur={caHandleBlur}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => caHandleChange(e)} />
                {caValues.vehicleReimbursement === "Yes" && (
                    <>
                        <SelectInput className={COL} disabled={!editable} name="reimbursementType"
                            label="Reimbursement Type" required value={caValues.reimbursementType}
                            options={[
                                { value: "", label: "Select Type" },
                                { value: "Vehicle", label: "Vehicle (flat daily rate)" },
                                { value: "Mileage", label: "Mileage (CRA rate)" },
                            ]}
                            error={!!caTouched.reimbursementType && !!caErrors.reimbursementType}
                            message={isTouched("reimbursementType") ? err("reimbursementType") : undefined} onBlur={caHandleBlur}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => caHandleChange(e)} />
                        {caValues.reimbursementType === "Vehicle" && (
                            <TextInput className={COL} disabled={!editable} name="vehicleRate"
                                label="Vehicle Daily Rate ($)" placeholder="Vehicle Daily Rate"
                                type="number" step="0.01" required value={caValues.vehicleRate}
                                error={!!caTouched.vehicleRate && !!caErrors.vehicleRate}
                                message={isTouched("vehicleRate") ? err("vehicleRate") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
                        )}
                    </>
                )}
                <SelectInput className={COL} disabled={!editable} name="loaApplicable"
                    label="Living Out Allowance (LOA) Applicable" value={caValues.loaApplicable}
                    options={[
                        { value: "", label: "Select" },
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                    ]}
                    error={!!caTouched.loaApplicable && !!caErrors.loaApplicable}
                    message={isTouched("loaApplicable") ? err("loaApplicable") : undefined} onBlur={caHandleBlur}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => caHandleChange(e)} />
                {caValues.loaApplicable === "Yes" && (
                    <TextInput className={COL} disabled={!editable} name="loaRate"
                        label="LOA Rate ($ per day)" placeholder="LOA Rate"
                        type="number" step="0.01" required value={caValues.loaRate}
                        error={!!caTouched.loaRate && !!caErrors.loaRate}
                        message={isTouched("loaRate") ? err("loaRate") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
                )}
            </>} />
        </div>

        <div className="form-section">
            <h5 className="form-section__title">B.3 — Travel</h5>
            <FormContainer inputs={<>
                <SelectInput className={COL} disabled={!editable} name="travelRequired" label="Travel Required" required
                    value={caValues.travelRequired}
                    options={[
                        { value: "", label: "Select" },
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                    ]}
                    error={!!caTouched.travelRequired && !!caErrors.travelRequired}
                    message={isTouched("travelRequired") ? err("travelRequired") : undefined} onBlur={caHandleBlur}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => caHandleChange(e)} />
            </>} />
        </div>

        <div className="form-section">
            <h5 className="form-section__title">B.4 — Other Expenses</h5>
            <FormContainer inputs={<>
                <TextAreaInput disabled={!editable} name="otherExpenses" label="Other Expenses"
                    placeholder="Describe any other approved expenses, or leave blank"
                    className={COL_FULL} value={caValues.otherExpenses} max={3000}
                    error={!!caTouched.otherExpenses && !!caErrors.otherExpenses}
                    message={isTouched("otherExpenses") ? err("otherExpenses") : undefined} onBlur={caHandleBlur} onChange={caHandleChange} />
            </>} />
        </div>
    </>
}

export default ConsultantAgreementForm